import LoadingScreen from "minimals-template/components/LoadingScreen"
import { Bound } from "lib/@components/binding/Bound"
import { Box, Link, Stack, Typography } from "@mui/material"
import { NoResultsContainer } from "lib/@components/no-results/no-results"
import { useProject } from "authoring/lib/hooks/use-project"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { FEATURE_FLAG_AUTHORING_PUBLISH } from "authoring/lib/enum/authoring-feature-flags.mjs"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { ProjectAccessButton } from "authoring/routes/project/components/user-access/access-button"
import { TreeToolbar } from "slot-definitions"
import { useParams } from "react-router"
import { navigateProjects } from "./utilities/project-navigation"
import { format } from "date-fns"
import { registerTab, Tabs } from "lib/@components/tabs"
import { SchedulesTab } from "./scoped-schedules/schedules-tab"
import { EditGroupsTab } from "./group/edit-groups-tab"
import { SendToLegacyButton } from "./plugins/send-to-legacy-button"
import { SetPublishedDateButton } from "./plugins/set-published-date-button"
import { isSendToLegacyBlockingSchedule } from "./lib/is-send-to-legacy-blocking-schedule"
import { UnlockWorkPackageButton } from "./plugins/unlock-work-package-button"
import { SendToReviewButton } from "./plugins/send-to-review-button"
import { StartReviewButton } from "./plugins/start-review-button"
import { getDraftOrPublishedSchedules } from "./controller/get-draft-or-published-schedules"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { refreshOnlyFor } from "lib/graphql/api"
import { useState } from "react"
import { Else, If } from "lib/@components/switch"
import { isEnabled } from "lib/@components/feature"
import { ProjectEditButton } from "./plugins/project-edit-button"
import { ProjectEditDialog } from "./components/project-edit-dialog"
import { SentToLegacyBanner } from "./components/sent-to-legacy-banner"
import { PublishToLegacyButton } from "./plugins/publish-to-legacy-button"
import { ArchivedBanner } from "./components/archived-banner"

export function Project() {
    const { projectId } = useParams()
    const [editOpen, setEditOpen] = useState(false)
    const { loading: projectLoading, project } = useProject({ id: projectId })
    const scheduleIds = project?.scopedScheduleIds ?? []
    const schedules = getDraftOrPublishedSchedules.useResults(
        { projectId, scheduleIds },
        refreshOnlyFor(projectId),
        withInitialValue(IS_LOADING)
    )
    const resolvedSchedules = Array.isArray(schedules) ? schedules : []
    const blockingSchedules = schedules === IS_LOADING ? [] : resolvedSchedules.filter(isSendToLegacyBlockingSchedule)
    const sendToLegacyBlocked = !scheduleIds.length || blockingSchedules.length > 0
    const publishMode = isEnabled(FEATURE_FLAG_AUTHORING_PUBLISH)

    TreeToolbar.usePlug(<ProjectAccessButton if={(c) => !c.isProjectPanel} priority={10} />)
    TreeToolbar.usePlug(<ProjectEditButton priority={20} />, [project])
    TreeToolbar.usePlug(<SendToReviewButton priority={50} />, [project, resolvedSchedules])
    TreeToolbar.usePlug(<StartReviewButton priority={55} />, [project, resolvedSchedules])
    TreeToolbar.usePlug(<SendToLegacyButton if={() => !publishMode} />, [project, resolvedSchedules, publishMode])
    TreeToolbar.usePlug(<PublishToLegacyButton if={() => publishMode} />, [project, resolvedSchedules, publishMode])
    TreeToolbar.usePlug(<SetPublishedDateButton priority={58} />, [project])
    TreeToolbar.usePlug(<UnlockWorkPackageButton priority={60} />, [project])

    if (projectLoading || schedules === IS_LOADING) return <LoadingScreen />
    if (!project) return <NoResultsContainer title="Work Package Details" message="Work Package Not Found" />
    const sentToLegacy = isLockedToLegacy(project.phase)

    registerTab({
        tab: "project",
        id: "schedules",
        title: <Box>Schedules</Box>,
        content: <SchedulesTab />,
    })
    registerTab({
        tab: "project",
        id: "groups",
        title: <Box>Edit Groups</Box>,
        content: <EditGroupsTab />,
    })
    return (
        <Bound
            target={project}
            blockingSchedules={blockingSchedules}
            resolvedSchedules={resolvedSchedules}
            scopedScheduleCount={scheduleIds.length}
            sendToLegacyBlocked={sendToLegacyBlocked}
            hideViewMode
            openProjectEdit={handleOpenProjectEdit}
        >
            <PageBreadcrumbContainer
                title={`${project?.name}`}
                description={
                    <Stack direction={"row"} sx={{ justifyContent: "space-between" }}>
                        <Typography>{project?.internalDescription}</Typography>
                        <Typography data-cy="project-modified-date">
                            {sentToLegacy
                                ? `Sent to Legacy: ${format(new Date(project?.sentToLegacy), "dd/MM/yy")}`
                                : `Last Update: ${format(new Date(project?._modified), "dd/MM/yy")}`}
                        </Typography>
                    </Stack>
                }
                parentCrumbs={
                    <Link sx={{ cursor: "pointer" }} onClick={navigateProjects}>
                        Work Packages
                    </Link>
                }
            >
                <If value={project.archived}>
                    <Box mt={1.5}>
                        <ArchivedBanner archivedAt={project.archivedAt} />
                    </Box>
                    <Else>
                        <If value={sentToLegacy}>
                            <Box mt={1.5}>
                                <SentToLegacyBanner sentToLegacy={project?.sentToLegacy} />
                            </Box>
                        </If>
                    </Else>
                </If>
                <Box flex={1}>
                    <Tabs id="project" panelHolder={<Box my={2} />} tabHolder={<Box />} />
                </Box>
            </PageBreadcrumbContainer>
            <If value={editOpen}>
                <ProjectEditDialog open={editOpen} setOpen={setEditOpen} project={project} />
            </If>
        </Bound>
    )

    function handleOpenProjectEdit() {
        setEditOpen(true)
    }
}
